
import { useModalProvider } from "../../../Providers/ModalProvider"
import { UploadProcessProvider } from "../../../Providers/UploadProcessProvider"
import UploadWindow from "./UploadsSubpages/UploadWindow"
import { useNavigate, useLocation } from "react-router-dom"
import { FRONTEND_LINKS } from "../../../Constants/FrontendLinks"
import { useState, useEffect, useCallback, useMemo } from "react"
import * as MdIcons from "react-icons/md"
import React from "react"
import { InfoMessage } from "../../../Constants/StyledComponents"
import { humanReadableSize, localizeDate } from "../../../Computation/utilFunctions"
import { useAuthProvider } from "../../../Providers/AuthProvider"
import { useBackendLinksProvider } from "../../../Providers/BackendLinksProvider"
import UnloadFilesModal from "./Subpages/UnloadFilesModal"
import { Deployment, useEnvironmentVariablesProvider } from "../../../Providers/EnvironmentVariablesProvider";
import { SelectPatientFilesModal } from "../../Data/Visualize/DataReview/Components/Modals/SelectPatientFilesModal/SelectPatientFilesModal"
import { normalizePatientFiles } from "../../../Components/FileSelectDropdown/normalize"
import { MobergButton, MobergButtonShape, MobergButtonVariant, MobergTheme, MobergRow, MobergText } from "../../../Moberg";
import { GridActionsCellItem } from "@mui/x-data-grid"
import { MdVisibility, MdFileDownload } from "react-icons/md"
import ExportConfirmModal from "./Export/ExportConfirmModal"
import ExportsListModal from "./Export/ExportsListModal"
import MobergSnackbar from "../../../Components/MobergSnackbar/MobergSnackbar"
import { IconButton, Tooltip } from "@mui/material"
import { useWorkspacesProvider } from "../../../Providers/WorkspacesProvider"
import PatientTabs from "./PatientTabs"
import ModernMUITable from '../../../Components/ModernMUITable';
import { MRNError } from "./MRNError"
import RemoteMonitoringModal from "./RemoteMonitoringModal"
import RMMobileSelectGateway from "../../RemoteMonitoring/mobileView/RMMobileSelectGateway"
import RMMobileAddPatient from "../../RemoteMonitoring/mobileView/RMMobileAddPatient"
import RMMobileGatewayMonitoring from "../../RemoteMonitoring/mobileView/RMMobileGatewayMonitoring"
import { URL_PARAMS, MODAL_TYPE } from "../../RemoteMonitoring/constants"
import {
    prefetchKeys,
    usePrefetchSnapshot,
} from "./DataReview/prefetch/hooks"
import {
    getActiveFile,
    getStorageSummary,
} from "./DataReview/prefetch/selectors"
import { useQueryClient } from "@tanstack/react-query"


// Shown on hover / toast when the Export action is used on a row whose source
// recording file is missing on disk (isExportable === false).
const NO_EXPORT_FILE_MESSAGE = "The patient file associated with this recording cannot be found."

const truncate = (value, maxLength) => {
    if (!value) {
        return ""
    }

    return value.length > maxLength
        ? `${value.slice(0, maxLength - 3)}...`
        : value
}

const HotStorageLink = ({ onClick }) => (
    <span
        onClick={onClick}
        style={{ color: 'var(--color-accent-interactive)', textDecoration: 'underline', cursor: 'pointer', marginLeft: '6px' }}
    >
        hot storage
    </span>
)

export default function DataVisualize() {
    const { createModal } = useModalProvider()
    const navigate = useNavigate()
    const authProvider = useAuthProvider()
    const { LINKS } = useBackendLinksProvider()
    const workspaceProvider = useWorkspacesProvider()
    const queryClient = useQueryClient()
    const prefetchQuery = usePrefetchSnapshot()
    const prefetchSnapshot = prefetchQuery.snapshot
    const location = useLocation()
    const setNavPage = () => {} // No-op for mobile components


    const { deployment, environmentVariables } = useEnvironmentVariablesProvider()
    const isClinical = deployment === Deployment.CLINICAL
    // Export is gated by the FEATURE_FLAG_EXPORT workspace flag (Backstage),
    // on top of the clinical-only + permission checks.
    const exportFeatureEnabled = isClinical && !!environmentVariables?.FEATURE_FLAG_EXPORT

    const getAdmissionStatus = useCallback(() => {
        if (location.state?.admission_status) {
            return location.state.admission_status
        }
        const urlParams = new URLSearchParams(location.search)
        return urlParams.get('admission_status') || 'admitted'
    }, [location.state?.admission_status, location.search])

    const [admissionStatus, setAdmissionStatus] = useState(() => {
        if (location.state?.admission_status) {
            return location.state.admission_status
        }
        const urlParams = new URLSearchParams(location.search)
        return urlParams.get('admission_status') || 'admitted'
    })

    useEffect(() => {
        const currentAdmissionStatus = getAdmissionStatus()
        setAdmissionStatus(currentAdmissionStatus)
        if (isClinical && !location.search.includes('admission_status')) {
            const currentPath = location.pathname
            const newSearch = location.search 
                ? `${location.search}&admission_status=${currentAdmissionStatus}`
                : `?admission_status=${currentAdmissionStatus}`
            navigate(`${currentPath}${newSearch}`, { replace: true })
        }
    }, [location, isClinical, navigate, getAdmissionStatus])

    const [hasOpenedModalFromUrl, setHasOpenedModalFromUrl] = useState(false)
    const [hasOpenedExportsFromUrl, setHasOpenedExportsFromUrl] = useState(false)
    const [refreshTrigger, setRefreshTrigger] = useState(0)
    const [exportToast, setExportToast] = useState({ open: false, message: "", severity: "info" })

    const isExportable = (row) => {
        // Non-clinical view: rich data_objects with obj_type/file_exists/filesize.
        const dataObjects = row.data_objects || []
        if (dataObjects.length > 0) {
            return dataObjects.some(
                f => f.obj_type === "CNS"
                    && f.file_exists
                    && Number(f.filesize || 0) > 0
            )
        }
        // Clinical view: data_objects isn't populated; fall back to row-level signals.
        const fileIds = Array.isArray(row.data_file_ids) ? row.data_file_ids : []
        const fileExists = Array.isArray(row.data_file_exists) ? row.data_file_exists : []
        const anyExists = fileIds.length > 0 && fileIds.some((_, i) => fileExists[i] !== false)
        return anyExists && Number(row.data_file_size || 0) > 0
    }

    const handleExportClick = (row) => {
        createModal(
            <ExportConfirmModal
                patient={row}
                onSuccess={(jobId) => setExportToast({
                    open: true,
                    message: `Export started — job #${jobId}. Track in Export Tracker.`,
                    severity: "success",
                })}
            />,
            { escClose: true, clickOutsideClose: true }
        )
    }

    // Detect if user is on mobile device
    const isMobileDevice = () => {
        const regex = /Mobi|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
        return regex.test(navigator.userAgent);
    }

    const [isMobile] = useState(isMobileDevice());

    // Auto-open Remote Monitoring Modal if URL contains modal=rm parameter
    useEffect(() => {
        const checkAndOpenModal = () => {
            const urlParams = new URLSearchParams(window.location.search)
            const modalParam = urlParams.get(URL_PARAMS.MODAL)
            
            // On desktop, open modal; on mobile, render will handle showing mobile components
            if (modalParam === MODAL_TYPE.REMOTE_MONITORING && !isMobile && !hasOpenedModalFromUrl) {
                setHasOpenedModalFromUrl(true)
                createModal(
                    <RemoteMonitoringModal />,
                    { 
                        escClose: false, 
                        clickOutsideClose: false,
                        afterClose: () => {
                            // Reset flag and clear URL params when modal is closed
                            setHasOpenedModalFromUrl(false)
                            const urlParams = new URLSearchParams(window.location.search)
                            urlParams.delete(URL_PARAMS.MODAL)
                            urlParams.delete(URL_PARAMS.STEP)
                            urlParams.delete(URL_PARAMS.GATEWAY_ID)
                            window.history.replaceState(null, '', `${window.location.pathname}?${urlParams.toString()}`)
                            
                            // Trigger data refresh to show newly added patients
                            setRefreshTrigger(prev => prev + 1)
                        }
                    }
                )
            } else if (modalParam !== MODAL_TYPE.REMOTE_MONITORING) {
                // Reset flag when modal parameter is removed
                setHasOpenedModalFromUrl(false)
            }
        }

        // Check on mount and when location changes (for deep linking)
        checkAndOpenModal()

        // Listen for custom event from button click
        window.addEventListener('modalUrlChange', checkAndOpenModal)
        
        return () => {
            window.removeEventListener('modalUrlChange', checkAndOpenModal)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasOpenedModalFromUrl])

    // Auto-open the Export Tracker modal when ?modal=exports is present.
    // Mirrors the Remote Monitoring pattern above — the Exports button on
    // PatientTabs just sets the URL param and dispatches `modalUrlChange`,
    // so a page reload deep-links straight back into the open modal.
    useEffect(() => {
        const checkAndOpenExportsModal = () => {
            const urlParams = new URLSearchParams(window.location.search)
            const modalParam = urlParams.get(URL_PARAMS.MODAL)

            if (modalParam === MODAL_TYPE.EXPORTS && !isMobile && !hasOpenedExportsFromUrl && exportFeatureEnabled && authProvider.permissions?.["export:view"]) {
                setHasOpenedExportsFromUrl(true)
                createModal(
                    // ModalsRenderer spreads component.props.style onto the modal
                    // wrapper, whose default is overflowY auto. That wrapper sits above
                    // the tracker's own header, so when it scrolls it carries the title
                    // and tabs off the top. The tracker sizes itself and scrolls the
                    // grid internally, so the wrapper must not scroll at all.
                    <ExportsListModal style={{ overflowY: "hidden" }} />,
                    {
                        escClose: true,
                        clickOutsideClose: true,
                        afterClose: () => {
                            setHasOpenedExportsFromUrl(false)
                            const urlParams = new URLSearchParams(window.location.search)
                            urlParams.delete(URL_PARAMS.MODAL)
                            window.history.replaceState(null, '', `${window.location.pathname}?${urlParams.toString()}`)
                        }
                    }
                )
            } else if (modalParam !== MODAL_TYPE.EXPORTS) {
                setHasOpenedExportsFromUrl(false)
            }
        }

        checkAndOpenExportsModal()
        window.addEventListener('modalUrlChange', checkAndOpenExportsModal)
        return () => {
            window.removeEventListener('modalUrlChange', checkAndOpenExportsModal)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasOpenedExportsFromUrl])


    function handleSelectedRow(selectedRow) {
        // normalizePatientFiles handles both patient shapes (data_objects and
        // the clinical parallel arrays) and sorts newest recording first.
        const patientFiles = normalizePatientFiles(selectedRow).filter(file =>
            // Clinical rows carry no obj_type; the review surface accepts them as-is.
            file.obj_type === undefined || file.obj_type === "CNS" || file.obj_type === "EDF"
        );

        createModal(
            <SelectPatientFilesModal
                patientFiles={patientFiles}
                selectedPatientKey={selectedRow.patient_key}
                selectedPatientID={selectedRow.patient_id}
                selectedSiteName={selectedRow.site_name}
                selectedPatientName={selectedRow.patient_name}
                autoSelectLatestFile={isClinical && admissionStatus === 'admitted'}
            />
        );
    }

    let columns = []

    if (isClinical) {
        columns = [
            {
                field: "view_button",
                headerName: "View",
                width: 90,
                minWidth: 90,
                maxWidth: 90,
                headerAlign: "center",
                align: "center",
                sortable: false,
                filterable: false,
                hideable: false,
                renderCell: (params) => {
                    // Don't render the View button during loading state
                    if (params.isLoading) {
                        return null;
                    }
                    return (
                        <MobergButton
                            variant={MobergButtonVariant.CLEAR}
                            theme={MobergTheme.BLUE}
                            shape={MobergButtonShape.RECTANGLE}
                            onClick={() => handleView(params.row)}
                            style={{ marginTop: '5px', height: '40px' }}
                        >
                            View
                        </MobergButton>
                    );
                },
            },
            {
                field: "patient_id",
                headerName: "Patient ID",
                minWidth: 310,
                visible: true,
                renderCell: (params) => (
                    <MobergRow gap="8px" horizontalAlign="space-between" style={{ height: "100%" }}>
                        <MobergText style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{params.row.patient_id}</MobergText>
                        <MRNError message={params.row.mrn_error} />
                    </MobergRow>
                )
            },
            {
                field: "patient_name",
                headerName: "Name",
                minWidth: 150,
                visible: true,
                valueGetter: (value, row) => row.patient_name || "Unknown"
            },
            {
                field: "data_file_size",
                flex: 0.15,
                headerName: "File size",
                valueGetter: (value, row) => {
                    return row.data_file_size ? humanReadableSize(Number(row.data_file_size)) : "Unknown";
                },
                minWidth: 80,
                maxWidth: 100,
                visible: true
            },
            {
                field: "annotation_count",
                flex: 0.25,
                headerName: "Annotations",
                valueGetter: (value, row) => row.annotation_count || 0,
                minWidth: 100,
                maxWidth: 120,
                visible: true
            },
            {
                field: "start_time",
                flex: 0.4,
                headerName: "Started",
                minWidth: 180,
                maxWidth: 200,
                visible: true,
                valueGetter: (value, row) => {
                    if (!row.start_time) return 'Unknown';
                    try {
                        return localizeDate(new Date(row.start_time), row.timezone?.[0]);
                    } catch (error) {
                        return 'Unknown';
                    }
                }
            },
            {
                field: "end_time",
                flex: 0.4,
                headerName: "Ended",
                visible: true,
                minWidth: 180,
                maxWidth: 200,
                valueGetter: (value, row) => {
                    if (!row.end_time) return 'Unknown';
                    try {
                        return localizeDate(new Date(row.end_time), row.timezone?.[0]);
                    } catch (error) {
                        return 'Unknown';
                    }
                }
            },
            // get monitoring time by subtracting start time from end time in days, hours, minutes, seconds format
            {
                field: "total_monitoring_time",
                flex: 0.4,
                headerName: "Duration",
                valueGetter: (value, row) => {
                    if (!row.total_monitoring_time) {
                        return 'Unknown'
                    }

                    const total_monitoring_time_seconds = row.total_monitoring_time / 1000 // Convert milliseconds to seconds
                    var days = Math.floor(total_monitoring_time_seconds / (3600 * 24))
                    var hours = Math.floor((total_monitoring_time_seconds - (days * 3600 * 24)) / 3600)
                    var minutes = Math.floor((total_monitoring_time_seconds - (days * 3600 * 24) - (hours * 3600)) / 60)

                    if (days > 0) return `${days} days ${hours} hrs ${minutes} mins`;
                    if (hours > 0) return `${hours} hrs ${minutes} mins`;
                    return `${minutes} mins`;
                },
                visible: true,
                minWidth: 200,

            },
            {
                field: "actions",
                width: 120,
                headerName: "Actions",
                type: "actions",
                hideable: false,
                getActions: params => [
                    <GridActionsCellItem
                        icon={<MdVisibility size={20} />}
                        label="Manage patient"
                        onClick={() => handleManagePatient(params.row)}
                        showInMenu
                    />,
                    ...(exportFeatureEnabled && authProvider.permissions?.['export:manage'] ? [
                        <GridActionsCellItem
                            icon={<MdFileDownload size={20} />}
                            label="Export"
                            title={isExportable(params.row) ? undefined : NO_EXPORT_FILE_MESSAGE}
                            sx={isExportable(params.row) ? undefined : { opacity: 0.5 }}
                            onClick={() => isExportable(params.row)
                                ? handleExportClick(params.row)
                                : setExportToast({ open: true, message: NO_EXPORT_FILE_MESSAGE, severity: "warning" })}
                            showInMenu
                        />
                    ] : []),
                ],
                minWidth: 100,
                visible: true
            },
        ]
    } else {
        columns = [
            {
                field: "view_button",
                headerName: "View",
                width: 90,
                minWidth: 90,
                maxWidth: 90,
                headerAlign: "center",
                align: "center",
                sortable: false,
                filterable: false,
                hideable: false,
                renderCell: (params) => {
                    // Don't render the View button during loading state
                    if (params.isLoading) {
                        return null;
                    }
                    return (
                        <MobergButton
                            variant={MobergButtonVariant.CLEAR}
                            theme={MobergTheme.BLUE}
                            shape={MobergButtonShape.RECTANGLE}
                            onClick={() => handleView(params.row)}
                            style={{ marginTop: '5px', height: '40px' }}
                        >
                            View
                        </MobergButton>
                    );
                },
            },
            { field: "patient_id", headerName: "Patient", minWidth: 180, visible: true, flex: 0.4},
            { 
                field: "site_id", 
                headerName: "Site", 
                minWidth: 200, 
                visible: true,
                valueGetter: (value, row) => `${row.site_identifier || ""}: ${row.site_name || ""}`
            },
            { 
                field: "project_id", 
                headerName: "Project", 
                minWidth: 140, 
                visible: false,
                valueGetter: (value, row) => `${row.project_identifier || ""}: ${row.project_name || ""}`
            },
            {
                field: "data_file_size",
                headerName: "File size",
                minWidth: 80,
                visible: true,
                valueGetter: (value, row) => humanReadableSize(Number(row.data_file_size || 0))
            },
            {
                field: "status",
                headerName: "Files loaded",
                minWidth: 100,
                visible: true,
                valueGetter: (value, row) => row.status || 'Unknown'
            },
            { 
                field: "annotation_count", 
                flex: 0.25, 
                headerName: "Annotations", 
                minWidth: 120, 
                visible: false,
                valueGetter: (value, row) => row.annotation_count || 0
            },
            { 
                field: "start_time", 
                headerName: "Start time", 
                minWidth: 160, 
                visible: true, 
                flex: 0.4,
                valueGetter: (value, row) => {
                    if (!row.start_time) return 'Unknown';
                    try {
                        return localizeDate(new Date(row.start_time), row.timezone?.[0]);
                    } catch (error) {
                        return 'Unknown';
                    }
                }
            },
            { 
                field: "end_time", 
                headerName: "End time", 
                visible: true, 
                flex: 0.4, 
                minWidth: 160,
                valueGetter: (value, row) => {
                    if (!row.end_time) return 'Unknown';
                    try {
                        return localizeDate(new Date(row.end_time), row.timezone?.[0]);
                    } catch (error) {
                        return 'Unknown';
                    }
                }
            },
            // get monitoring time by subtracting start time from end time in days, hours, minutes, seconds format
            {
                field: "total_monitoring_time",
                flex: 0.4,
                headerName: "Total monitoring time",
                valueGetter: (value, row) => {
                    if (!row.total_monitoring_time) {
                        return 'Unknown'
                    }

                    const total_monitoring_time_seconds = row.total_monitoring_time / 1000 // Convert milliseconds to seconds
                    var days = Math.floor(total_monitoring_time_seconds / (3600 * 24))
                    var hours = Math.floor((total_monitoring_time_seconds - (days * 3600 * 24)) / 3600)
                    var minutes = Math.floor((total_monitoring_time_seconds - (days * 3600 * 24) - (hours * 3600)) / 60)

                    if (days > 0) return `${days} days ${hours} hrs ${minutes} mins`;
                    if (hours > 0) return `${hours} hrs ${minutes} mins`;
                    return `${minutes} mins`;
                },
                visible: true,
                minWidth: 200
            },
            {
                field: "timestamp",
                flex: 0.4,
                headerName: "Most recent upload",
                visible: false,
                minWidth: 200,
                valueGetter: (value, row) => {
                    if (!row.timestamp) return 'Unknown';
                    try {
                        return localizeDate(new Date(row.timestamp));
                    } catch (error) {
                        return 'Unknown';
                    }
                }
            },
            {
                field: "first_name",
                flex: 0.4,
                headerName: "Uploaded by",
                visible: false,
                minWidth: 180,
                sortable: false,
                valueGetter: (value, row) => `${row.first_name || ""} ${row.last_name || ""}`
            },
            {
                field: "actions",
                width: 120,
                headerName: "Actions",
                type: "actions",
                hideable: false,
                getActions: params => [
                    <GridActionsCellItem
                        icon={<MdVisibility size={20} />}
                        label="Manage patient"
                        onClick={() => handleManagePatient(params.row)}
                        showInMenu
                    />,
                    ...(exportFeatureEnabled && authProvider.permissions?.['export:manage'] ? [
                        <GridActionsCellItem
                            icon={<MdFileDownload size={20} />}
                            label="Export"
                            title={isExportable(params.row) ? undefined : NO_EXPORT_FILE_MESSAGE}
                            sx={isExportable(params.row) ? undefined : { opacity: 0.5 }}
                            onClick={() => isExportable(params.row)
                                ? handleExportClick(params.row)
                                : setExportToast({ open: true, message: NO_EXPORT_FILE_MESSAGE, severity: "warning" })}
                            showInMenu
                        />
                    ] : []),
                ],
                visible: true
            },
        ]
    }

    function handleView(row) {
        handleSelectedRow(row);
    }

    function handleManagePatient(row) {
        navigate(`${FRONTEND_LINKS.SUBPAGES.DATA.SUBPAGES.VISUALIZE.SUBPAGES.PATIENTS.MAIN}?patient_id=${row.patient_key}`, { state: row })
    }


    function renderUploadModal() {
        createModal(
            <UploadProcessProvider>
                <UploadWindow escClose={false} clickOutsideClose={false} />
            </UploadProcessProvider>
        )
    }

    const currentlyPrefetchingFile = useMemo(
        () => getActiveFile(prefetchSnapshot.files),
        [prefetchSnapshot.files]
    )

    const { currentInventorySize, maxInventorySize } = useMemo(() => {
        const { currentSizeBytes, maxSizeBytes } = getStorageSummary(prefetchSnapshot)

        return {
            currentInventorySize: humanReadableSize(currentSizeBytes),
            maxInventorySize: humanReadableSize(maxSizeBytes),
        }
    }, [prefetchSnapshot])

    const patientTableToolbarProps = {
        createButton: { title: "Upload patient", isShow: false },
        deleteButton: { isShow: false },
        filterButton: { title: "Filter", isShow: false },
        exportButton: { isShow: false },
        searchInput: { isShow: true },
        columnButton: { isShow: true },
        mainTable: true
    }

    const queryProps = {
        queryKey: `patients-${refreshTrigger}`,
        endpoint: isClinical 
            ? LINKS.DATA.PROFILING.GET_PATIENTS_CLINICAL 
            : LINKS.DATA.PROFILING.GET_PATIENTS,
        body: {
            ...(isClinical && admissionStatus ? { admission_status: admissionStatus, feature_flag_adt_integration: environmentVariables.FEATURE_FLAG_ADT_INTEGRATION } : {}),
            workspace_name: workspaceProvider.selectedWorkspace
        },
        optionalParams: {}
    }

    function openUnloadFilesModal() {
        createModal(
            <UnloadFilesModal />
        )
    }

    function renderStorageNotification() {
        const storageNotificationText = () => {
            if (!prefetchQuery.isFetched) {
                return <>Loading hot storage status...</>
            }

            let prefetchStatus = ""
            if (currentlyPrefetchingFile) {
                const prefetchPatientId = truncate(currentlyPrefetchingFile?.patient_id || "", 24)
                const prefetchProgress = Number(currentlyPrefetchingFile?.percent_loaded || 0).toFixed(2)

                prefetchStatus = currentlyPrefetchingFile?.state === "cancelled"
                    ? `Currently cancelling preload for patient ID ${prefetchPatientId}`
                    : `Currently loading patient ID ${prefetchPatientId} (${prefetchProgress}% complete)`
            }

            return <>{currentInventorySize} of {maxInventorySize} have been used in <HotStorageLink onClick={openUnloadFilesModal} />. {prefetchStatus}</>
        }

        return (
            <div style={{ padding: '10px 30px 0px', display: 'flex', justifyContent: isClinical ? 'flex-end' : 'space-between', alignItems: 'center' }}>

                {!isClinical && <InfoMessage style={{ flex: 1, marginRight: '20px'  }}>
                    <p style={{  marginLeft: '-10px' }}>
                        <Tooltip title="This indicates how much data is directly accessible to review. Data uploaded to the system is stored in cold storage by default. Data needs to be moved from cold storage to hot storage for it to be reviewed.">
                            <IconButton>
                                <MdIcons.MdInfo color="var(--color-accent-interactive)" />
                            </IconButton>
                        </Tooltip>
                        {storageNotificationText()}
                    </p>
                    <MdIcons.MdRefresh
                        size={18}
                        style={{ right: "10px", cursor: "pointer", color: "var(--color-accent-interactive)" }}
                        onClick={() => queryClient.invalidateQueries({
                            queryKey: prefetchKeys.snapshot(workspaceProvider.selectedWorkspace),
                        })}
                    />
                </InfoMessage>}

                {!isClinical && (
                    <MobergButton
                        variant={MobergButtonVariant.FILLED}
                        theme={MobergTheme.BLUE}
                        shape={MobergButtonShape.RECTANGLE}
                        onClick={renderUploadModal}
                        disabled={!authProvider.permissions?.['patient:manage']}
                    >
                        Upload patient
                    </MobergButton>
                )}
            </div>
        )
    }


    const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
        if (isClinical) {
            return {
                project_id: false,
                annotation_count: false,
                end_time: admissionStatus !== 'admitted',
                timestamp: false,
                first_name: false,
                patient_name: true,
                view_button: true,
                actions: true,
            };
        } else {
            return {
                project_id: false,
                annotation_count: false,
                total_monitoring_time: true,
                timestamp: false,
                first_name: false,
                view_button: true,
                actions: true,
            };
        }
    });

    useEffect(() => {
        if (isClinical) {
            setColumnVisibilityModel(prevModel => ({
                ...prevModel,
                end_time: admissionStatus !== 'admitted'
            }));
        }
    }, [admissionStatus, isClinical]);

    // Render mobile components if on mobile and modal param is present
    const urlParams = new URLSearchParams(location.search)
    const modalParam = urlParams.get(URL_PARAMS.MODAL)
    const stepParam = urlParams.get(URL_PARAMS.STEP)
    
    if (isMobile && modalParam === MODAL_TYPE.REMOTE_MONITORING) {
        const renderMobileStep = () => {
            switch (stepParam) {
                case 'SELECT_GATEWAY':
                    return <RMMobileSelectGateway setNavPage={setNavPage} />
                case 'ADD_PATIENT':
                    return <RMMobileAddPatient setNavPage={setNavPage} />
                case 'MONITORING':
                    return <RMMobileGatewayMonitoring setNavPage={setNavPage} />
                default:
                    return <RMMobileSelectGateway setNavPage={setNavPage} />
            }
        }
        
        return renderMobileStep()
    }

    return (
        <div style={{ backgroundColor: "var(--color-surface-primary)" }}>
            {renderStorageNotification()}

            {isClinical && <PatientTabs />}
            <div style={{ 
                position: 'relative', 
                padding: '10px 30px 20px', 
                height: 'calc(100vh - 100px)',
                minHeight: '500px',
                maxHeight: '82vh'
            }}>
                <ModernMUITable
                    columns={columns}
                    tableToolbarProps={patientTableToolbarProps}
                    {...queryProps}
                    supportsPageSize={true}
                    isRowSelectable={() => false}
                    isCheckboxSelection={false}
                    checkboxSelection={false}
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={setColumnVisibilityModel}
                    height="100%"
                    paginationMode="server"
                    sortingMode="server"
                    defaultSortModel={[
                        { field: 'patient_id', sort: 'asc' }
                    ]}
                    initialState={{
                        pagination: {
                            paginationModel: { page: 0, pageSize: 10 }
                        }
                    }}
                    persistStateInUrl={true}
                />
            </div>
            <MobergSnackbar
                open={exportToast.open}
                onClose={() => setExportToast(prev => ({ ...prev, open: false }))}
                message={exportToast.message}
                severity={exportToast.severity}
            />
        </div>
    )
}
